import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Search,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  TrendingUp,
  AlertCircle,
  BarChart3,
  ChevronRight,
  Users,
  Timer,
  Target,
  Share2,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';
import { useTheme } from '../context/ThemeContext';
import ExcelExportButton from '../components/ExcelExportButton';

// ── Import from your existing api.js ─────────────────────────────────────────
import { dashboardApi, authHelpers, fetchApiDirect } from '../services/api';

const { RangePicker } = DatePicker;

// ── Conversion-specific API calls (all use type=CONVERSION) ──────────────────
const conversionApi = {
  getKPI: (startDate, endDate, extra = {}) => {
    const p = new URLSearchParams({ type: 'CONVERSION' });
    if (startDate)     p.append('startDate', startDate);
    if (endDate)       p.append('endDate',   endDate);
    if (extra.process) p.append('process',   extra.process);
    if (extra.shift)   p.append('shift',     extra.shift);
    return fetchApiDirect(`/dashboard/business/kpi?${p}`);
  },

  getMonthlyPerformance: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'CONVERSION' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/business/monthly-performance?${p}`);
  },

  getFunnel: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'CONVERSION' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/business/funnel?${p}`);
  },

  getManagersFocus: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'CONVERSION' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/focus?${p}`);
  },

  getAllManagers: (startDate, endDate, page = 0, size = 20) => {
    const p = new URLSearchParams({ type: 'CONVERSION', page, size });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/all?${p}`);
  },

  getTeamInsights: (managerId, startDate, endDate) => {
    const p = new URLSearchParams({ type: 'CONVERSION' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/${managerId}/team-insights?${p}`);
  },

  getTeamLeadAgents: (tlId, startDate, endDate, page = 0, size = 10) => {
    const p = new URLSearchParams({ type: 'CONVERSION', page, size });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/teamleads/${tlId}/agents?${p}`);
  },

  getAssignees: (assigneeType) =>
    fetchApiDirect(`/targets/assignees?targetType=CONVERSION&assigneeType=${assigneeType}`),

  assignTarget: (body) =>
    fetchApiDirect(`/targets/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
};

// ── Format helpers ────────────────────────────────────────────────────────────
const fmtN   = (v) => { if (v == null) return '—'; const n = Number(v); return isNaN(n) ? String(v) : n.toLocaleString('en-IN'); };
const fmtPct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';
const fmtTime = (s) => {
  if (s == null) return '—';
  const sec = Math.round(Number(s));
  const m   = Math.floor(sec / 60);
  return m > 0 ? `${m}m ${sec % 60}s` : `${sec}s`;
};

// ── Status config ─────────────────────────────────────────────────────────────
const statusConfig = (p) => {
  if (p >= 100) return { label: 'Exceeding',      color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' };
  if (p >= 90)  return { label: 'Excellent',       color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' };
  if (p >= 75)  return { label: 'On track',        color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' };
                return { label: 'Needs review',    color: '#f97316', bg: '#fff7ed', border: '#fed7aa' };
};

// ─────────────────────────────────────────────────────────────────────────────
const MISConversionTarget = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const location = useLocation();

  // ── state ──────────────────────────────────────────────────────────────────
 const [dates, setDates] = useState([
  dayjs().startOf('month'),
  dayjs().endOf('month'),
]);
  const [processFilter, setProcessFilter] = useState('');
  const [shiftFilter,   setShiftFilter]   = useState('');
  const [searchQuery,   setSearchQuery]   = useState('');

  const [kpi,          setKpi]          = useState(null);
  const [monthly,      setMonthly]      = useState(null);
  const [funnel,       setFunnel]       = useState(null);
  const [managers,     setManagers]     = useState([]);
  const [teamInsights, setTeamInsights] = useState(null);
  const [selectedMgr,  setSelectedMgr]  = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [agentsByLead, setAgentsByLead] = useState({});

  const [loading, setLoading] = useState({
    kpi: false, monthly: false, funnel: false, managers: false, insights: false, export: false,
  });
  const [errors, setErrors] = useState({});

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignment, setAssignment] = useState({ type: 'TEAM_LEAD', id: '', target: '' });
  const [assignees,  setAssignees]  = useState([]);
  const [assigning,  setAssigning]  = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', isError: false });

  // ── helpers ────────────────────────────────────────────────────────────────
  const startDate = dates?.[0]?.format('YYYY-MM-DD');
  const endDate   = dates?.[1]?.format('YYYY-MM-DD');

  const showToast = (message, isError = false) => {
    setToast({ show: true, message, isError });
    setTimeout(() => setToast({ show: false, message: '', isError: false }), 3000);
  };
  const setLoad = (k, v) => setLoading(p => ({ ...p, [k]: v }));
  const setErr  = (k, v) => setErrors(p  => ({ ...p, [k]: v }));

  // ── fetchers ───────────────────────────────────────────────────────────────
  const loadKPI = useCallback(async () => {
    setLoad('kpi', true); setErr('kpi', null);
    try   { setKpi(await conversionApi.getKPI(startDate, endDate, { process: processFilter, shift: shiftFilter })); }
    catch (e) { setErr('kpi', e.message); }
    finally   { setLoad('kpi', false); }
  }, [startDate, endDate, processFilter, shiftFilter]);

  const loadMonthly = useCallback(async () => {
    setLoad('monthly', true); setErr('monthly', null);
    try   { setMonthly(await conversionApi.getMonthlyPerformance(startDate, endDate)); }
    catch (e) { setErr('monthly', e.message); }
    finally   { setLoad('monthly', false); }
  }, [startDate, endDate]);

  const loadFunnel = useCallback(async () => {
    setLoad('funnel', true); setErr('funnel', null);
    try   { setFunnel(await conversionApi.getFunnel(startDate, endDate)); }
    catch (e) { setErr('funnel', e.message); }
    finally   { setLoad('funnel', false); }
  }, [startDate, endDate]);

  const loadManagers = useCallback(async () => {
    setLoad('managers', true); setErr('managers', null);
    try {
      const data = await conversionApi.getManagersFocus(startDate, endDate);
      setManagers(Array.isArray(data) ? data : data?.managers || data?.data || []);
    } catch (e) { setErr('managers', e.message); }
    finally { setLoad('managers', false); }
  }, [startDate, endDate]);

  const loadTeamInsights = useCallback(async (managerId) => {
    setLoad('insights', true); setErr('insights', null);
    try {
      const data = await conversionApi.getTeamInsights(managerId, startDate, endDate);
      setTeamInsights(data);
      setSelectedMgr(managerId);
    } catch (e) { setErr('insights', e.message); }
    finally { setLoad('insights', false); }
  }, [startDate, endDate]);

  const loadAgents = useCallback(async (tlId) => {
    if (agentsByLead[tlId]) return;
    try {
      const data   = await conversionApi.getTeamLeadAgents(tlId, startDate, endDate);
      const agents = Array.isArray(data) ? data : data?.agents || data?.data || [];
      setAgentsByLead(p => ({ ...p, [tlId]: agents }));
    } catch (e) { showToast(`Agents load failed: ${e.message}`, true); }
  }, [agentsByLead, startDate, endDate]);

  const loadAssignees = useCallback(async () => {
    try {
      const data = await conversionApi.getAssignees(assignment.type);
      setAssignees(Array.isArray(data) ? data : data?.assignees || []);
    } catch (e) { showToast(`Assignees load failed: ${e.message}`, true); }
  }, [assignment.type]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadKPI(); loadMonthly(); loadFunnel(); loadManagers();
    setTeamInsights(null); setSelectedMgr(null);
    setExpandedLead(null); setAgentsByLead({});
  }, [startDate, endDate, processFilter, shiftFilter]);

  useEffect(() => {
    if (isAssignModalOpen) loadAssignees();
  }, [isAssignModalOpen, assignment.type]);

  // ── actions ────────────────────────────────────────────────────────────────
  const handleViewAllManagers = async () => {
    try {
      const data = await conversionApi.getAllManagers(startDate, endDate);
      setManagers(Array.isArray(data) ? data : data?.managers || data?.content || []);
    } catch (e) { showToast(`Failed: ${e.message}`, true); }
  };

  const handleExportExcel = async () => {
    showToast('Generating export…');
    try {
      await dashboardApi.exportDashboardData({ dashboardType: 'CONVERSION', startDate, endDate });
      showToast('Excel downloaded!');
    } catch {
      const rows = kpi ? [
        { Metric: 'Total Leads Assigned',   Value: fmtN(kpi.totalLeadsAssigned ?? kpi.totalLeadsAssigned) },
        { Metric: 'Gross Conversion Rate',  Value: fmtPct(kpi.grossConversionRate ?? kpi.grossConversion) },
        { Metric: 'Net Conversion Rate',    Value: fmtPct(kpi.netConversionRate ?? kpi.netConversion) },
        { Metric: 'Qualified Leads',        Value: fmtN(kpi.qualifiedLeads) },
        { Metric: 'Connect Rate',           Value: fmtPct(kpi.connectRate) },
        { Metric: 'Lead to Order %',        Value: fmtPct(kpi.leadToOrderRate) },
        { Metric: 'Order to Delivery %',    Value: fmtPct(kpi.orderToDeliveryRate ?? kpi.deliveryRate) },
        { Metric: 'Avg Response Time (s)',  Value: kpi.avgResponseTime ?? '—' },
      ] : [{ Metric: 'No data', Value: '' }];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Conversion');
      XLSX.writeFile(wb, `conversion_target_${startDate}.xlsx`);
      showToast('Excel downloaded (local data).');
    }
  };

  const toggleLeadExpansion = (tlId) => {
    if (expandedLead === tlId) { setExpandedLead(null); return; }
    setExpandedLead(tlId);
    loadAgents(tlId);
  };

  const submitAssignment = async () => {
    if (!assignment.id || !assignment.target) {
      showToast('Please select a member and enter a target.', true);
      return;
    }
    setAssigning(true);
    try {
      const user = authHelpers.getCurrentUser();
      await conversionApi.assignTarget({
        targetType:   'CONVERSION',
        assigneeType: assignment.type,
        targetValue:  Number(assignment.target),
        startDate,
        endDate,
        assigneeIds:  [Number(assignment.id)],
        createdById:  user?.id ?? 99,
      });
      showToast('Target assigned successfully!');
      setIsAssignModalOpen(false);
      setAssignment({ type: 'TEAM_LEAD', id: '', target: '' });
      loadManagers();
      if (selectedMgr) loadTeamInsights(selectedMgr);
    } catch (e) {
      showToast(`Assignment failed: ${e.message}`, true);
    } finally { setAssigning(false); }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const achieved    = monthly?.achieved ?? kpi?.grossConversionRate ?? kpi?.grossConversion ?? 0;
  const target      = monthly?.target   ?? kpi?.target ?? kpi?.conversionTarget ?? 0;
  const pct         = target > 0 ? Math.round((achieved / target) * 100) : 0;
  const gap         = achieved - target; // positive = above target for conversion
  const aboveTarget = gap >= 0;
  const achievingText = aboveTarget ? 'Above Target' : 'Below Target';

  const funnelSteps = funnel ? [
    { label: 'Leads',     value: funnel.totalLeads     ?? funnel.leads           ?? 0, color: `${currentTheme.color}60` },
    { label: 'Orders',    value: funnel.orders         ?? funnel.ordersPlaced    ?? 0, color: `${currentTheme.color}80` },
    { label: 'Verified',  value: funnel.verified       ?? funnel.verifiedOrders  ?? 0, color: `${currentTheme.color}A0` },
    { label: 'Delivered', value: funnel.delivered      ?? funnel.deliveredOrders ?? 0, color: currentTheme.color },
  ] : [];

  const Spinner   = () => <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" style={{ color: currentTheme.color }} />;
  const ErrBanner = ({ msg }) => (
    <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">{msg}</div>
  );

  const tabClass = (tab) =>
    `px-4 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 ${
      location.pathname === tab
        ? 'text-white shadow-md'
        : (isDark ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100/50')
    }`;

  const tabStyle = (tab) => location.pathname === tab ? { backgroundColor: currentTheme.color } : {};

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col gap-6 p-4 md:p-6 min-h-screen transition-colors duration-300 ${!isDark ? 'bg-[#F8FAFC]' : ''}`} style={isDark ? { background: currentTheme.cssGradient } : {}}>

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-xl font-bold text-gray-800">Business Dashboard</h1>
          <div className={`custom-range-picker rounded-lg overflow-hidden border ${isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white'}`}>
            <RangePicker
              value={dates}
              onChange={(vals) => { setDates(vals); setAgentsByLead({}); }}
              format="MMM DD, YYYY"
              allowClear={false}
              suffixIcon={<ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-400'}`} />}
              style={{ borderRadius: '8px', border: 'none', fontWeight: 'bold', background: 'transparent' }}
              className={isDark ? 'dark-range-picker' : ''}
            />
          </div>
        </div>
        <div className={`flex items-center p-1 rounded-xl gap-1 ${isDark ? 'bg-black/40' : 'bg-[#E9EEF2]'}`}>
          <button className={tabClass('/mis-reports/business-dashboard')} style={tabStyle('/mis-reports/business-dashboard')} onClick={() => navigate('/mis-reports/business-dashboard')}>Orders target</button>
          <button className={tabClass('/mis-reports/revenue-target')}    style={tabStyle('/mis-reports/revenue-target')} onClick={() => navigate('/mis-reports/revenue-target')}>Revenue target</button>
          <button className={tabClass('/mis-reports/conversion-target')} style={tabStyle('/mis-reports/conversion-target')} onClick={() => navigate('/mis-reports/conversion-target')}>Conversion target</button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className={`p-4 rounded-xl shadow-sm border flex flex-wrap items-center gap-4 ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Search Employee</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="ID, name or code…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all ${isDark ? 'bg-black/20 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'}`}
            />
          </div>
        </div>

        {[
          { label: 'Process', value: processFilter, setter: setProcessFilter, options: [{ v: '', l: 'All processes' }, { v: 'FLead', l: 'FLead' }, { v: 'RTO', l: 'RTO' }] },
          { label: 'Shift',   value: shiftFilter,   setter: setShiftFilter,   options: [{ v: '', l: 'All shifts' }, { v: 'MORNING', l: 'Morning' }, { v: 'EVENING', l: 'Evening' }, { v: 'NIGHT', l: 'Night' }] },
        ].map(({ label, value, setter, options }) => (
          <div key={label} className="min-w-[140px]">
            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">{label}</label>
            <div className="relative">
              <select value={value} onChange={(e) => setter(e.target.value)}
                className={`w-full appearance-none border rounded-lg px-3 py-2 pr-8 text-sm font-semibold focus:outline-none focus:ring-2 cursor-pointer ${isDark ? 'bg-black/20 border-white/10 text-white hover:bg-white/10 [&>option]:bg-[#12141a]' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                style={{ ringColor: currentTheme.color + '20' }}
              >
                {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        ))}

        <div className="flex gap-2 ml-auto self-end">
          <ExcelExportButton onClick={handleExportExcel} isDownloading={loading.export} />
          <button onClick={() => { showToast('Generating PDF…'); setTimeout(() => window.print(), 500); }} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-bold transition-all font-black ${isDark ? 'bg-black/20 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* ── KPI Row 1 ── */}
      {errors.kpi && <ErrBanner msg={errors.kpi} />}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Leads Assigned',  value: fmtN(kpi?.totalLeadsAssigned ?? kpi?.totalLeadsAssigned ?? kpi?.totalAssignedLeads),          icon: Users,        trend: kpi?.leadsTrend,       sub: `${startDate} – ${endDate}` },
          { label: 'Gross Conversion Rate', value: fmtPct(kpi?.grossConversionRate ?? kpi?.grossConversion ?? kpi?.grossConversionPct), icon: Target,       trend: kpi?.grossTrend,       sub: kpi?.grossTarget != null ? `Target: ${fmtPct(kpi.grossTarget)} achieved` : null, highlight: true },
          { label: 'Net Conversion Rate',   value: fmtPct(kpi?.netConversionRate   ?? kpi?.netConversion),                          icon: Share2,       trend: kpi?.netTrend,         sub: kpi?.netTarget != null ? `Target: ${fmtPct(kpi.netTarget)} achieved` : null },
          { label: 'Qualified Leads',       value: fmtN(kpi?.qualifiedLeads),                                                        icon: CheckCircle2, trend: kpi?.qualifiedTrend,   sub: kpi?.leadQualityPct != null ? `${fmtPct(kpi.leadQualityPct)} lead quality` : null },
          { label: 'Connect Rate',          value: fmtPct(kpi?.connectRate),                                                         icon: Timer,        trend: kpi?.connectTrend,     sub: kpi?.connectTarget != null ? `Target: ${fmtPct(kpi.connectTarget)} achieved` : null },
        ].map((card, i) => (
          <div key={i} className={`p-5 rounded-2xl shadow-sm border transform transition-all hover:scale-[1.02] ${card.highlight ? 'border-orange-100 ring-2 ring-orange-100 ring-offset-2' : ''} ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-50'}`}>
            <div className="flex justify-between items-start mb-4">
              <p className="text-xs font-bold text-gray-400 uppercase leading-none max-w-[120px]">{card.label}</p>
              {card.trend != null && (
                <span className="text-[10px] font-black text-green-500 bg-green-50 px-1.5 py-0.5 rounded-md">
                  {typeof card.trend === 'number' ? fmtPct(card.trend) : card.trend}
                </span>
              )}
            </div>
            <h3 className="text-2xl font-black text-gray-800 mb-1">
              {loading.kpi ? <Spinner /> : (card.value ?? '—')}
            </h3>
            {card.sub && <p className="text-[10px] text-gray-400 font-bold">{card.sub}</p>}
          </div>
        ))}
      </div>

      {/* ── Process & Efficiency Row ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Lead to Order %',          value: fmtPct(kpi?.leadToOrderRate ?? kpi?.leadToOrder),                    color: 'from-purple-400 to-purple-600', raw: kpi?.leadToOrderRate ?? 0 },
          { label: 'Order to Delivery %',      value: fmtPct(kpi?.orderToDeliveryRate ?? kpi?.deliveryRate),               color: 'from-blue-400 to-blue-600',     raw: kpi?.orderToDeliveryRate ?? kpi?.deliveryRate ?? 0 },
          { label: 'Overall Funnel Efficiency',value: fmtPct(kpi?.funnelEfficiency ?? kpi?.efficiency ?? kpi?.grossConversionPct), color: 'from-purple-400 to-purple-600', raw: kpi?.funnelEfficiency ?? 0, isPurple: true, sub: kpi?.efficiencyTarget != null ? `Target: ${fmtPct(kpi.efficiencyTarget)} achieved` : null },
          { label: 'Avg. Lead Response Time',  value: fmtTime(kpi?.avgResponseTime ?? kpi?.avgAHT),                         isTime: true, sub: kpi?.responseTimeDelta != null ? `-${fmtTime(kpi.responseTimeDelta)} vs last month` : null },
        ].map((item, i) => (
          <div key={i} className={`p-6 rounded-2xl shadow-sm border ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-50'}`}>
            <p className="text-xs font-bold text-gray-400 uppercase mb-4">{item.label}</p>
            <h3 className={`text-2xl font-black mb-4 ${item.isPurple ? 'text-purple-600' : 'text-gray-800'}`}>
              {loading.kpi ? <Spinner /> : (item.value ?? '—')}
            </h3>
            {!item.isTime ? (
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-700`}
                  style={{ width: `${Math.min(100, Math.max(0, item.raw ?? 0))}%` }} />
              </div>
            ) : (
              item.sub && <p className="text-xs font-bold text-gray-400">{item.sub}</p>
            )}
            {item.sub && !item.isTime && <p className="text-[10px] font-black text-purple-400 mt-2">{item.sub}</p>}
          </div>
        ))}
      </div>

      {/* ── Monthly Performance + Funnel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Achievement card */}
        <div className={`lg:col-span-2 p-8 rounded-[32px] shadow-sm border flex flex-col justify-between overflow-hidden relative ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-50'}`}>
          <div>
            <div className="flex items-center gap-2 mb-8">
              <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                <BarChart3 className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <h2 className="text-sm font-black text-gray-400 uppercase tracking-widest">Monthly Conversion Performance</h2>
            </div>

            {loading.monthly ? (
              <div className="py-8 text-center text-gray-400 text-sm"><Spinner />Loading…</div>
            ) : errors.monthly ? (
              <ErrBanner msg={errors.monthly} />
            ) : (
              <>
                <div className="flex items-end gap-3 mb-10">
                  <h1 className="text-7xl font-black leading-none" style={{ color: currentTheme.color }}>{fmtPct(achieved)}</h1>
                  <span className="text-2xl font-black text-gray-300 mb-2">/ {fmtPct(target)} Target Rate</span>
                </div>
                <div className="space-y-4">
                  {aboveTarget ? (
                    <div className="flex items-center gap-2 text-green-500 font-bold">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-sm uppercase tracking-wider font-black">+{fmtPct(Math.abs(gap))} Above Target</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-orange-500 font-bold">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm uppercase tracking-wider font-black">{fmtPct(Math.abs(gap))} Below Target</span>
                    </div>
                  )}
                  {(monthly?.daysRemaining ?? kpi?.daysRemaining) != null && (
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      Goal Deadline: {monthly?.daysRemaining ?? kpi?.daysRemaining} days remaining
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Achievement badge */}
          <div className="absolute right-10 top-1/2 -translate-y-1/2 p-10 rounded-[40px] border text-center" style={{ backgroundColor: `${currentTheme.color}10`, borderColor: `${currentTheme.color}20` }}>
            <span className="text-7xl font-black" style={{ color: currentTheme.color }}>{pct}%</span>
            <div className="w-full h-1 rounded-full my-3" style={{ backgroundColor: currentTheme.color }} />
            <p className="text-lg font-black uppercase tracking-[4px]" style={{ color: currentTheme.color }}>Achievement</p>
          </div>
        </div>

        {/* Funnel */}
        <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-50">
          <h2 className="text-sm font-black text-gray-900 mb-8">{dates?.[0]?.format('MMMM')} sales funnel</h2>
          {loading.funnel ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm"><Spinner />Loading…</div>
          ) : errors.funnel ? (
            <ErrBanner msg={errors.funnel} />
          ) : funnelSteps.length === 0 ? (
            <p className="text-sm text-gray-400 text-center mt-8">No funnel data</p>
          ) : (
            <>
              <div className="flex flex-col items-center gap-2">
                {funnelSteps.map((step, i) => {
                  const topW   = 280 - i * 44;
                  const botW   = 280 - (i + 1) * 44;
                  const svgW   = 300;
                  const tX     = (svgW - topW) / 2;
                  const bX     = (svgW - botW) / 2;
                  const pctLbl = funnelSteps[0].value > 0 ? `${((step.value / funnelSteps[0].value) * 100).toFixed(1)}%` : '0%';
                  const path   = `M ${tX} 0 L ${tX + topW} 0 Q ${tX + topW - 6} 22 ${bX + botW} 44 L ${bX} 44 Q ${tX + 6} 22 ${tX} 0 Z`;
                  return (
                    <div key={step.label} className="relative group" style={{ width: svgW, height: 44 }}>
                      <svg width={svgW} height={44} viewBox={`0 0 ${svgW} 44`} style={{ display: 'block' }}>
                        <path d={path} fill={step.color} className="transition-all duration-300 group-hover:brightness-105" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-black tracking-wide pointer-events-none">
                        {step.label}: {fmtN(step.value)} ({pctLbl})
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-10 pt-6 border-t border-gray-50 flex justify-around">
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">Conversion</p>
                  <p className="text-lg font-black text-gray-800">{fmtPct(funnel?.conversionRate ?? funnel?.conversion)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">Efficiency</p>
                  <p className="text-lg font-black" style={{ color: currentTheme.color }}>{fmtPct(funnel?.efficiency ?? funnel?.efficiencyRate)}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Managers Conversion Focus ── */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 tracking-tight">Managers Conversion Focus</h2>
          <button onClick={handleViewAllManagers} className="flex items-center text-sm font-bold hover:opacity-80" style={{ color: currentTheme.color }}>
            View all managers <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
        {errors.managers && <ErrBanner msg={errors.managers} />}
        {loading.managers ? (
          <div className="text-center py-10 text-gray-400"><Spinner />Loading managers…</div>
        ) : managers.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No manager data available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managers
              .filter((m) => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return m.managerName?.toLowerCase().includes(q) || String(m.managerId ?? m.id ?? '').includes(q);
              })
              .map((manager) => {
                const mId  = manager.id ?? manager.managerId;
                // For conversion: achieved/target are percentages
                const mPct = manager.target > 0 ? Math.round((manager.achieved / manager.target) * 100) : 0;
                const sc   = statusConfig(mPct);
                return (
                  <div key={mId} className={`p-6 rounded-2xl border-2 shadow-sm transition-all hover:shadow-lg bg-gradient-to-br ${isDark ? 'from-black/20 to-black/10 text-white' : 'from-white to-gray-50/30'} ${selectedMgr === mId ? (isDark ? 'border-blue-500/50' : 'border-blue-200') : (isDark ? 'border-white/10' : manager.borderColor || 'border-transparent')}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-gray-200 rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${manager.managerName}`} alt={manager.managerName} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-800 leading-none mb-1.5 uppercase tracking-tight">{manager.managerName}</h4>
                        <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">{manager.role ?? manager.designation ?? 'Manager'}</p>
                      </div>
                    </div>
                    <div className="flex justify-between mb-6 px-1">
                      <div>
                        <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-1.5">Target Rate</p>
                        <p className="text-xl font-black text-slate-700">{fmtPct(manager.target)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-1.5">Current Rate</p>
                        <p className="text-xl font-black text-slate-700">{fmtPct(manager.achieved)}</p>
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="flex justify-between items-end mb-2.5">
                        <span className="text-[11px] font-black tracking-tighter" style={{ color: sc.color }}>{mPct}% achievement</span>
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{sc.label}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(mPct, 100)}%`, backgroundColor: sc.color }} />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-50 bg-[#F8FAFC] -mx-6 -mb-6 px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Target className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-700 leading-none uppercase">Team Insights</p>
                          <p className="text-[9px] text-gray-400 mt-1 font-bold">
                            {manager.teamLeadCount ?? manager.leads ?? '—'} TLs • {manager.agentCount ?? manager.agents ?? '—'} agents
                          </p>
                        </div>
                      </div>
                      <button onClick={() => loadTeamInsights(mId)} className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all shadow-sm ${isDark ? 'bg-white/10 border-white/10 hover:bg-white/20' : 'bg-white border-gray-100 hover:bg-slate-50'}`}>
                        {loading.insights && selectedMgr === mId
                          ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: currentTheme.color }} />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />
                        }
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ── Team Insights ── */}
      {(teamInsights || loading.insights) && (
        <div className={`p-8 rounded-[40px] border shadow-sm mt-8 ${isDark ? 'bg-black/20 text-white' : 'bg-white'}`} style={{ borderColor: `${currentTheme.color}10` }}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10 bg-[#F8FAFC] p-5 rounded-2xl border border-gray-50">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}10` }}>
                <Users className="w-6 h-6" style={{ color: currentTheme.color }} />
              </div>
              <div>
                <h2 className="text-xl font-black text-[#1E293B] tracking-tight">
                  {teamInsights?.managerName ? `Team insights: ${teamInsights.managerName}'s group` : 'Team insights'}
                </h2>
                <p className="text-[11px] font-bold text-gray-300 uppercase tracking-[2px] mt-1">
                  {teamInsights?.teamLeadCount ?? '—'} team leads • {teamInsights?.agentCount ?? '—'} agents
                </p>
              </div>
            </div>
            <button onClick={() => setIsAssignModalOpen(true)} className={`border px-6 py-2.5 rounded-2xl text-xs font-black shadow-sm transition-all active:scale-95 ${isDark ? 'bg-black/20 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              Manage assignment
            </button>
          </div>

          {errors.insights && <ErrBanner msg={errors.insights} />}

          {loading.insights ? (
            <div className="text-center py-8 text-gray-400"><Spinner />Loading team insights…</div>
          ) : (
            <div className="space-y-6">
              {(teamInsights?.teamLeads ?? teamInsights?.teamLead ?? []).map((lead) => {
                const tlId   = lead.id ?? lead.employeeId;
                const tlPct  = lead.target > 0 ? Math.round((lead.achieved / lead.target) * 100) : 0;
                const sc     = statusConfig(tlPct);
                const isExp  = expandedLead === tlId;
                const agents = agentsByLead[tlId] ?? lead.agents ?? [];
                // gap: positive = above target for conversion
                const tlGap  = lead.gap ?? (lead.achieved - lead.target);
                const gapAbove = tlGap >= 0;

                return (
                  <div key={tlId} className="space-y-4">
                    <div
                      onClick={() => toggleLeadExpansion(tlId)}
                      className={`p-6 rounded-[28px] border flex items-center cursor-pointer transition-all hover:shadow-md ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'} ${isExp ? (isDark ? 'shadow-white/5 border-blue-500/50' : 'shadow-lg border-gray-200') : ''}`}
                      style={isExp ? { borderColor: `${currentTheme.color}20` } : {}}
                    >
                      <ChevronRight className={`w-4 h-4 text-gray-200 mr-6 transition-transform duration-300 ${isExp ? 'rotate-90' : ''}`} style={isExp ? { color: currentTheme.color } : {}} />
                      <div className="w-14 h-14 rounded-full bg-gray-100 mr-5 flex-shrink-0 overflow-hidden border border-gray-50 shadow-sm">
                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.name}`} alt={lead.name} />
                      </div>

                      <div className="grid grid-cols-5 flex-1 items-center gap-2">
                        <div>
                          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Team Lead</p>
                          <p className="text-[14px] font-black text-slate-700">{lead.name}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Conv. Target</p>
                          <p className="text-[14px] font-black text-slate-700">{fmtPct(lead.target)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Achieved</p>
                          <p className="text-[14px] font-black text-slate-700">{fmtPct(lead.achieved)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Connect Rate</p>
                          <p className="text-[14px] font-black text-orange-400">{fmtPct(lead.connectRate ?? lead.connect)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mb-2">Gap</p>
                          <p className={`text-[14px] font-black ${gapAbove ? 'text-green-500' : 'text-red-500'}`}>
                            {gapAbove ? '+' : ''}{fmtPct(tlGap)}
                          </p>
                        </div>
                      </div>

                      <div className="px-5 py-2.5 rounded-2xl text-center border min-w-[170px]"
                        style={{ color: sc.color, backgroundColor: sc.bg, borderColor: sc.border }}>
                        <p className="text-[9px] font-black uppercase tracking-wider">{sc.label.toUpperCase()} ({tlPct}%)</p>
                      </div>
                    </div>

                    {/* Agents sub-table */}
                    {isExp && (
                      <div className="animate-in fade-in slide-in-from-top-2 duration-300 px-10 pb-4">
                        <div className="grid grid-cols-7 gap-4 px-10 mb-3 text-[9px] font-bold text-gray-300 uppercase tracking-widest">
                          <div className="col-span-1">Agent name</div>
                          <div>Leads</div>
                          <div>Orders</div>
                          <div>Conversion %</div>
                          <div>Connect Rate</div>
                          <div>Qualified %</div>
                          <div>Response Time</div>
                        </div>

                        {agents.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-4">No agent data</p>
                        ) : (
                          <div className="space-y-2">
                            {agents.map((agent, j) => {
                              const conv    = agent.conversionRate ?? agent.conversion ?? agent.conv ?? 0;
                              const connect = agent.connectRate    ?? agent.connect    ?? 0;
                              const aColor  = conv >= 25 ? '#22c55e' : conv >= 20 ? '#3b82f6' : '#ef4444';
                              return (
                                <div key={j} className="flex items-center h-16 bg-[#F8FAFC]/40 rounded-2xl border border-gray-50 relative overflow-hidden transition-all hover:bg-white hover:shadow-sm">
                                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l`} style={{ backgroundColor: aColor }} />
                                  <div className="grid grid-cols-7 gap-4 flex-1 items-center px-10">
                                    <div className="flex items-center gap-4">
                                      <div className="w-9 h-9 rounded-full bg-white overflow-hidden shadow-sm border border-gray-100">
                                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.name}`} alt={agent.name} />
                                      </div>
                                      <span className="text-xs font-bold text-slate-700 truncate">{agent.name}</span>
                                    </div>
                                    <div className="text-xs font-bold text-slate-700">{fmtN(agent.leads ?? agent.totalLeads)}</div>
                                    <div className="text-xs font-black text-slate-700">{fmtN(agent.orders ?? agent.ordersPlaced)}</div>
                                    <div className="text-xs font-black text-green-500">{fmtPct(conv)}</div>
                                    <div className="flex flex-col gap-1.5">
                                      <span className="text-[10px] font-black text-orange-400">{fmtPct(connect)}</span>
                                      <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-orange-400" style={{ width: `${Math.min(100, connect)}%` }} />
                                      </div>
                                    </div>
                                    <div className="text-xs font-black text-purple-600">{fmtPct(agent.qualifiedRate ?? agent.qualified)}</div>
                                    <div className="text-xs font-black text-slate-700">{fmtTime(agent.avgResponseTime ?? agent.responseTime)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="mt-8 flex items-center justify-between">
                          <p className="text-[11px] font-bold text-gray-300 tracking-tight">Showing {agents.length} agents</p>
                          <button onClick={() => navigate(`/mis-reports/teamleads/${tlId}/agents`)} className="flex items-center gap-1.5 text-xs font-black hover:opacity-80 transition-transform" style={{ color: currentTheme.color }}>
                            View all agent list <ChevronRight className="w-4 h-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Target Assignment Modal ── */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-gray-950/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl border border-white/20 overflow-hidden">
            <div className="p-10">
              <div className="flex items-start justify-between mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}10`, color: currentTheme.color }}>
                    <Target className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-gray-900 tracking-tight">Assign Conversion Target</h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mt-1.5">Monthly conversion goals</p>
                  </div>
                </div>
                <button onClick={() => setIsAssignModalOpen(false)} className={`w-12 h-12 flex items-center justify-center rounded-2xl transition-all ${isDark ? 'bg-white/10 text-gray-400 hover:bg-red-500/20 hover:text-red-400' : 'bg-gray-50 text-gray-300 hover:bg-red-50 hover:text-red-500'}`}>
                  <AlertCircle className="w-6 h-6 rotate-45" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Level toggle */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4" style={{ color: currentTheme.color }} />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select assignment level</label>
                  </div>
                  <div className={`flex p-1.5 rounded-[22px] border ${isDark ? 'bg-black/40 border-white/5' : 'bg-gray-50 border-gray-100/50'}`}>
                    {[
                      { label: 'Team lead',        val: 'TEAM_LEAD' },
                      { label: 'Individual agent', val: 'EMPLOYEE' },
                    ].map((opt) => (
                      <button key={opt.val} onClick={() => setAssignment(a => ({ ...a, type: opt.val, id: '' }))}
                        className={`flex-1 py-3.5 px-6 rounded-[18px] text-xs font-black transition-all duration-300 ${
                          assignment.type === opt.val
                            ? 'bg-white shadow-lg border'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        style={assignment.type === opt.val ? { color: currentTheme.color, borderColor: `${currentTheme.color}20` } : {}}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assignee */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Search className="w-4 h-4 text-orange-500" />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Select {assignment.type === 'TEAM_LEAD' ? 'team lead' : 'agent'}
                    </label>
                  </div>
                  <div className="relative">
                    <select value={assignment.id} onChange={(e) => setAssignment(a => ({ ...a, id: e.target.value }))}
                      className={`w-full border rounded-2xl px-6 py-4 text-sm font-bold focus:outline-none focus:ring-4 transition-all appearance-none cursor-pointer ${isDark ? 'bg-black/40 border-white/10 text-white focus:ring-purple-500/20 focus:bg-black/60' : 'bg-gray-50 border-gray-100 text-gray-700 focus:ring-purple-500/10 focus:bg-white'}`}>
                      <option value="">Select a member…</option>
                      {assignees.map((a) => (
                        <option key={a.id ?? a.employeeId} value={a.id ?? a.employeeId}>
                          {a.name}{a.teamName ? ` — ${a.teamName}` : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" />
                  </div>
                </div>

                {/* Target value */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Conversion target (%)</label>
                  </div>
                  <div className="relative group">
                    <input type="number" step="0.1" min="0" max="100" placeholder="e.g. 25.5" value={assignment.target}
                      onChange={(e) => setAssignment(a => ({ ...a, target: e.target.value }))}
                      className={`w-full border rounded-2xl px-6 py-4 text-base font-black placeholder:text-gray-300 focus:outline-none focus:ring-4 transition-all pr-16 ${isDark ? 'bg-black/40 border-white/10 text-white focus:ring-purple-500/20 focus:bg-black/60' : 'bg-gray-50 border-gray-100 text-gray-800 focus:ring-purple-500/10 focus:bg-white'}`} />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-40 group-focus-within:opacity-100 transition-opacity">
                      <span className="text-[10px] font-black text-purple-600">% RATE</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 flex items-center gap-4">
                <button onClick={() => setIsAssignModalOpen(false)} className={`flex-1 py-4 px-6 rounded-2xl text-xs font-black transition-all tracking-wider ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}>
                  DISCARD
                </button>
                <button onClick={submitAssignment} disabled={assigning}
                  className="flex-[2] bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:opacity-60 text-white px-10 py-5 rounded-3xl text-sm font-black shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 group">
                  {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
                  APPLY CONVERSION TARGET
                  {!assigning && <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast.show && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-10 fade-in duration-500">
          <div className={`px-8 py-4 rounded-[24px] shadow-2xl flex items-center gap-4 ${toast.isError ? 'bg-red-600' : 'bg-gray-900/90 backdrop-blur-xl border border-white/10'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${toast.isError ? 'bg-red-400' : 'bg-green-500'} shadow-lg`}>
              {toast.isError ? <AlertCircle className="w-4 h-4 text-white" /> : <TrendingUp className="w-4 h-4 text-white" />}
            </div>
            <p className="text-sm font-black text-white tracking-wide">{toast.message}</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default MISConversionTarget;